import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import classNames from "clsx";
import { FaChevronDown, FaCubes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getUserType } from "../../../utils/authutils";

function SidebarLink({ link, buildingParts, dropdownOpen, setDropdownOpen, isCollapsed }) {
  const { t } = useTranslation();
  const location = useLocation();
  const userType = getUserType();
  const [searchParams] = useSearchParams();
  const pathname = location.pathname;
  let isActive = pathname === link.path;
  if (link.key === "dashboard") {
    if (pathname === "/dashboard" || pathname === "/dashboard/") {
      isActive = true;
    }
  }

  const overviewImageId = searchParams.get("overviewImageId");
  const buildingId = searchParams.get("buildingId");
  const reportType = searchParams.get("reportType");
  const projectId = searchParams.get("projectId");
  const navigate = useNavigate();

  const showDropdown =
    dropdownOpen && buildingParts && buildingParts.length > 0 && link.key === "custProperty";

  const getStepFromPath = (path) => {
    if (path?.includes("/dashboard/building/information")) {
      return "info";
    } else if (path?.includes("/dashboard/buildings/overview")) {
      return "building";
    } else if (path?.includes("/dashboard/building/finance")) {
      return "financial";
    } else if (path?.includes("/dashboard/building/maintainence")) {
      return "maintanence";
    } else if (path?.startsWith("/dashboard/building/building-part/")) {
      return "property";
    }
    return searchParams.get("step");
  };

  const step = getStepFromPath(link.path);

  const dynamicPath =
    (userType === "client" || userType === "client_member" || userType === "customer") &&
    link.key !== "projects" &&
    link.key !== "dashboard" &&
    link.key !== "managebids" &&
    link.key !== "document" &&
    link.key !== "damage-management"
      ? `${link.path}?step=${step || ""}&overviewImageId=${overviewImageId || ""}&buildingId=${
          buildingId || ""
        }&reportType=${reportType || ""}&projectId=${projectId || ""}`
      : link.path;

  const handleDropdownToggle = (e) => {
    if (link.key === "custProperty") {
      e.preventDefault();
      setDropdownOpen((prev) => !prev);
      navigate(
        `${link.path}?step=${step || ""}&overviewImageId=${overviewImageId || ""}&buildingId=${
          buildingId || ""
        }&reportType=${reportType || ""}&projectId=${projectId || ""}`,
      );
    }
  };

  return (
    <>
      {link.key === "custProperty" ? (
        <button
          type="button"
          onClick={handleDropdownToggle}
          className="w-full flex items-center gap-3 font-light pl-[16px] pr-4 py-2 rounded-sm text-sm transition-colors text-left"
        >
          <span
            className={classNames(
              "flex items-center justify-center w-10 h-10 rounded-full shrink-0",
              isActive ? "bg-[#EAF4EF] border border-[#3D6C50] text-[#3D6C50]" : "text-gray-500",
            )}
          >
            {link.icon}
          </span>

          <span
            className="text-sm text-gray-700 whitespace-nowrap overflow-hidden"
            style={{
              maxWidth: isCollapsed ? 0 : 180,
              opacity: isCollapsed ? 0 : 1,
              visibility: isCollapsed ? "hidden" : "visible",
              transition: isCollapsed
                ? "max-width 250ms ease-in-out, opacity 100ms ease-in-out"
                : "max-width 250ms ease-in-out, opacity 200ms ease-in-out 80ms",
            }}
          >
            {t(`main.sidebar.${link.key}`, link.label)}
          </span>

          {buildingParts && buildingParts.length > 0 && (
            <FaChevronDown
              className={`ml-auto text-xs transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
            />
          )}
        </button>
      ) : (
        <Link
          to={dynamicPath}
          className="relative flex items-center transition-colors w-full pl-[16px] gap-3"
        >
          {isActive && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1.5 rounded-r-full bg-[#3D6C50]" />
          )}

          <span
            className={classNames(
              "flex items-center justify-center w-10 h-10 rounded-full shrink-0",
              isActive ? "bg-[#EAF4EF] border border-[#3D6C50] text-[#3D6C50]" : "text-gray-500",
            )}
          >
            {link.icon}
          </span>

          <span
            className="text-sm text-gray-700 whitespace-nowrap overflow-hidden"
            style={{
              maxWidth: isCollapsed ? 0 : 180,
              opacity: isCollapsed ? 0 : 1,
              visibility: isCollapsed ? "hidden" : "visible",
              transition: isCollapsed
                ? "max-width 250ms ease-in-out, opacity 100ms ease-in-out"
                : "max-width 250ms ease-in-out, opacity 200ms ease-in-out 80ms",
            }}
          >
            {t(`main.sidebar.${link.key}`, link.label)}
          </span>
        </Link>
      )}

      {showDropdown && (
        <div
          className="pl-4 ml-2 border-gray-300 flex flex-col gap-2 overflow-y-auto"
          style={{ marginTop: "4px", maxHeight: "160px" }}
        >
          {buildingParts.map((part) => {
            const partPath = `/dashboard/building/overview/building-part/${part.id}`;
            const isPartActive = pathname === partPath;
            return (
              <Link
                key={part.id}
                to={`${partPath}?overviewImageId=${overviewImageId || ""}&buildingId=${
                  buildingId || ""
                }&partialImageId=${part.partialImageId || ""}&reportType=${reportType || ""}&projectId=${projectId || ""}`}
                className={classNames(
                  "text-xs py-1 px-2 rounded transition-colors font-medium flex items-center gap-1",
                  isPartActive
                    ? "bg-white border-r-4 border-[#3D6C50] text-textSecondaryColor"
                    : "hover:bg-gray-100 text-gray-700",
                )}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <FaCubes />
                {!isCollapsed && t(`main.partialDetails.${part.name.toLowerCase()}`, part.name)}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}

export default SidebarLink;
